import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "./EndOfYear.scss";
import { Parallax, ParallaxLayer, IParallax } from "@react-spring/parallax";
import { useHistory } from "react-router-dom";
import PagesControls from "./Components/PagesControls";
import {
  getFirstMenuItemPageIndex,
  showMenuPages,
  slidesOrder,
} from "./EOYUtils";
import { gql, useQuery } from "@apollo/client";
import Sound from "react-sound";

import {
  IntroPage,
  FinalPage,
  IntegrationsPage,
  AggregationsPage,
  CvesPage,
  TopCommonIssuesPage,
  ResourcesPage,
  TagsPage,
  TotalAutomationsPage,
  TopAssigneesPage,
  TopQueuesPage,
  AccomplishmentsPage,
  NoQueuesPage,
} from "./Pages";
import useLogger from "../Logger/useLogger";
import ExportedEndOfYear from "./Exported/ExportedEndOfYear";
import { DOWNLOADING_PDF_TOAST_ID } from "../Tools/useExportPDF";
import { showToast, type ShowToastProps } from "../Forms/Dialogs/Toast/Toast";

// S3 arn: (dev) arn:aws:s3:::seem-eoy-2024/forward-together.mp3
const audioSrc =
  "https://seem-eoy-2024.s3.eu-central-1.amazonaws.com/forward-together.mp3";

export const GET_DATA = gql`
  query eoy {
    special_occasions(key: "end_of_year_2025") {
      value
    }
  }
`;

const pageKeyToComponent = {
  INTEGRATIONS_PAGE: IntegrationsPage,
  AGGREGATIONS_PAGE: AggregationsPage,
  CVES_PAGE: CvesPage,
  TOP_COMMON_ISSUES_PAGE: TopCommonIssuesPage,
  RESOURCES_PAGE: ResourcesPage,
  TAGS_PAGE: TagsPage,
  TOTAL_AUTOMATIONS_PAGE: TotalAutomationsPage,
  TOP_ASSIGNEES_PAGE: TopAssigneesPage,
  TOP_QUEUES_PAGE: TopQueuesPage,
  ACCOMPLISHMENTS_PAGE: AccomplishmentsPage,
  NO_QUEUES_PAGE: NoQueuesPage,
};

const EndOfYear: React.FC = () => {
  const parallax = useRef<IParallax>(null);
  const { push } = useHistory();
  const [currentPage, setCurrentPage] = useState(0);
  const [audioPlayingState, setAudioPlayingState] = useState<
    "PLAYING" | "STOPPED" | "PAUSED"
  >("PLAYING");
  const { logError } = useLogger();

  const [isMuted, setIsMuted] = useState(false);

  const [shouldDownload, setShouldDownload] = useState(false);

  const { data } = useQuery(GET_DATA, {
    fetchPolicy: "cache-first",
  });

  const customerData = useMemo(() => {
    if (!!data) {
      if (!!data?.special_occasions?.value) {
        try {
          return JSON.parse(data?.special_occasions?.value);
        } catch (e) {
          logError(e, "EndOfYear: Error parsing customer data");
          push("/dashboard");
          return null;
        }
      } else {
        push("/dashboard");
      }
    }

    return null;
  }, [data]);

  const pages = useMemo(() => {
    if (!!customerData) {
      const pageKeys = Object.keys(customerData);
      if (!customerData.TOTAL_AUTOMATIONS_PAGE) {
        const indexToAdd = pageKeys.indexOf("TAGS_PAGE");
        pageKeys.splice(indexToAdd + 1, 0, "NO_QUEUES_PAGE");
      }

      return pageKeys.sort(
        (a, b) => slidesOrder.indexOf(a) - slidesOrder.indexOf(b)
      );
    } else {
      return [];
    }
  }, [customerData]);

  const numOfPages = useMemo(() => pages.length, [pages]);

  const backToPlatform = () => push("/dashboard");

  useEffect(() => {
    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
      const height = parallax.current?.space ?? 0 * 0.9;
      const scrollablePages = numOfPages + 2;
      const scrollHeight = height * scrollablePages;

      const scrollTop = (e.target as HTMLDivElement).scrollTop;
      const percentScrolled = scrollTop / scrollHeight;
      const page = Math.floor(percentScrolled * scrollablePages);

      if (currentPage !== page) {
        setCurrentPage(page);
      }
    };

    if (!!parallax?.current) {
      const container = parallax.current.container.current;
      container.addEventListener("scroll", handleScroll);

      return () => {
        container?.removeEventListener("scroll", handleScroll);
      };
    }
  }, [parallax, numOfPages]);

  const currentPageKey = useMemo(
    () => pages[currentPage - 1],
    [currentPage, pages]
  );

  const onMuteClick = () => {
    setAudioPlayingState(isMuted ? "PLAYING" : "PAUSED");
    setIsMuted((prev) => !prev);
  };

  const onDownloadClick = useCallback(() => {
    const typedShowToast = showToast as (props: ShowToastProps) => string;
    const toastId = typedShowToast({
      position: "bottom-right",
      hideProgressBar: false,
      autoClose: false,
      closeOnClick: true,
      className: "toast-bottom-right",
      draggable: false,
      component: (
        <div className={"downloading-indicator"}>
          <i className={"seem-icon-download"} />
          <span>Downloading PDF</span>
        </div>
      ),
    });

    localStorage.setItem(DOWNLOADING_PDF_TOAST_ID, JSON.stringify(toastId));

    const timeout = setTimeout(() => setShouldDownload(true), 200);

    return () => clearTimeout(timeout);
  }, []);

  return (
    <>
      <div className={"eoy-container"}>
        <Sound
          url={audioSrc}
          playStatus={audioPlayingState}
          loop={true}
          autoLoad={true}
        />
        {!!customerData && !!numOfPages && (
          <Parallax pages={numOfPages + 2} ref={parallax}>
            <ParallaxLayer offset={0}>
              <IntroPage
                getNextPage={() => parallax.current?.scrollTo(1)}
                onExit={backToPlatform}
                onMute={onMuteClick}
                isMuted={isMuted}
                onDownload={onDownloadClick}
              />
            </ParallaxLayer>
            {pages?.map((key, index) => {
              const Component = pageKeyToComponent[
                key as keyof typeof pageKeyToComponent
              ] as React.FC<{ data: any }>;
              return (
                <React.Fragment key={key}>
                  <ParallaxLayer offset={index + 1}>
                    <Component
                      data={customerData[key]}
                      {...(key === "TOP_QUEUES_PAGE"
                        ? { hasAssignees: pages.includes("TOP_ASSIGNEES_PAGE") }
                        : {})}
                    />
                  </ParallaxLayer>
                </React.Fragment>
              );
            })}
            <ParallaxLayer offset={numOfPages + 1}>
              <FinalPage
                onExit={backToPlatform}
                onMute={onMuteClick}
                isMuted={isMuted}
                onDownload={onDownloadClick}
              />
            </ParallaxLayer>
            <ParallaxLayer sticky={{ start: 1, end: numOfPages }}>
              <PagesControls
                displayMenu={showMenuPages.includes(currentPageKey)}
                currentPageKey={currentPageKey}
                currentPage={currentPage}
                getNextPage={() => parallax.current?.scrollTo(currentPage + 1)}
                getPreviousPage={() =>
                  parallax.current?.scrollTo(currentPage - 1)
                }
                gotoPage={(menuItem) =>
                  parallax.current?.scrollTo(
                    getFirstMenuItemPageIndex(pages, menuItem) + 1
                  )
                }
                onExit={backToPlatform}
                onMute={onMuteClick}
                isMuted={isMuted}
                onDownload={onDownloadClick}
              />
            </ParallaxLayer>
          </Parallax>
        )}
      </div>
      <ExportedEndOfYear
        data={customerData}
        pages={pages.filter((page) => page !== "NO_QUEUES_PAGE")}
        shouldDownload={shouldDownload}
        onDownloadEnd={() => setShouldDownload(false)}
      />
    </>
  );
};

export default EndOfYear;
